import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import MobileComparisonAccordionTable from './MobileComparisonAccordionTable';
import Image from '../../Image/Image';

const TICK_ICON_URL =
  'https://assets.askhippo.co.za/Icons/common/TickCircle.svg';
const CROSS_ICON_URL =
  'https://assets.askhippo.co.za/Icons/common/Delete-Square-New.svg';

const getBinaryIconLabel = (icon) => (icon === 'check' ? 'Yes' : 'No');

const isLinkCell = (cell) =>
  Boolean(cell && typeof cell === 'object' && cell.url && 'text' in cell);

const renderCellContent = (cell, isDesktop, align = 'center') => {
  if (cell && typeof cell === 'object' && cell.icon) {
    if (cell.icon === 'cross') {
      return (
        <Image
          width={22}
          height={22}
          src={CROSS_ICON_URL}
          alt="No"
          sx={{
            width: isDesktop ? '22px' : '18px',
            height: isDesktop ? '22px' : '18px',
            display: 'block',
            margin: align === 'left' ? '0' : '0 auto',
          }}
        />
      );
    }

    if (cell.icon === 'check') {
      return (
        <Image
          width={22}
          height={22}
          src={TICK_ICON_URL}
          alt="Yes"
          sx={{
            width: isDesktop ? '22px' : '18px',
            height: isDesktop ? '22px' : '18px',
            display: 'block',
            margin: align === 'left' ? '0' : '0 auto',
          }}
        />
      );
    }

    return (
      <Box
        component="div"
        role="img"
        aria-label={getBinaryIconLabel(cell.icon)}
        sx={{
          color: '#162F3B',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontStyle: 'normal',
          fontSize: isDesktop ? '1.5rem' : '1.25rem',
          lineHeight: 1,
          m: 0,
          width: '100%',
          textAlign: align,
          display: 'block',
        }}
      >
        {cell.icon === 'check' ? '✓' : '✕'}
      </Box>
    );
  }

  const linkCell = isLinkCell(cell);

  return (
    <Typography
      {...(linkCell ? { component: Link, to: cell.url } : {})}
      sx={{
        color: '#162F3B',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        fontStyle: 'normal',
        m: 0,
        width: '100%',
        textAlign: align,
        fontSize: '0.875rem',
        lineHeight: isDesktop ? '1.5rem' : '1.25rem',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        ...(linkCell
          ? {
              textDecoration: 'none',
              '&:hover': {
                color: '#C7CA00',
                textDecoration: 'underline',
              },
            }
          : {}),
      }}
    >
      {linkCell ? cell.text : cell}
    </Typography>
  );
};

export const MedicalAidSeoDetailTable = ({ table, isDesktop }) => {
  if (!isDesktop) {
    return (
      <MobileComparisonAccordionTable table={table} defaultOpenIndex={0} />
    );
  }

  const headers = table?.headers || [];
  const colWidths = table?.colWidths || [];
  const minWidth = table?.minWidth || (headers.length > 4 ? '920px' : '760px');
  const desktopWidth = table?.desktopWidth || '757px';
  const mobileWidth = table?.mobileWidth || minWidth;
  const align = table?.align === 'left' ? 'left' : 'center';

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: isDesktop ? desktopWidth : '100%',
        borderRadius: isDesktop ? '28px' : '20px',
        border: '1px solid #DDE2E6',
        overflowX: isDesktop ? 'visible' : 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        boxSizing: 'border-box',
        backgroundColor: '#FFFFFF',
      }}
    >
      <table
        style={{
          width: isDesktop ? '100%' : mobileWidth,
          minWidth: isDesktop ? '0' : mobileWidth,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {colWidths.length > 0 ? (
          <colgroup>
            {colWidths.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
        ) : null}

        <thead>
          <tr>
            {headers.map((header, index) => (
              <th
                key={index}
                style={{
                  backgroundColor: '#C7CA00',
                  color: '#162F3B',
                  textAlign: align,
                  padding: isDesktop ? '14px 12px' : '12px 10px',
                  fontWeight: 700,
                  fontSize: isDesktop ? '1rem' : '0.875rem',
                  lineHeight: isDesktop ? '1.25rem' : '1.125rem',
                  borderRight: '1px solid #F1F2D0',
                  borderTopLeftRadius:
                    index === 0 ? (isDesktop ? '28px' : '20px') : 0,
                  borderTopRightRadius:
                    index === headers.length - 1
                      ? isDesktop
                        ? '28px'
                        : '20px'
                      : 0,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {(table.rows || []).map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  style={{
                    padding: isDesktop ? '18px 12px' : '14px 10px',
                    textAlign: align,
                    verticalAlign: 'middle',
                    borderTop: '1px solid #DDE2E6',
                    borderRight:
                      cellIndex === row.length - 1
                        ? 'none'
                        : '1px solid #DDE2E6',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  {renderCellContent(cell, isDesktop, align)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {table.footer ? (
        <Box
          sx={{
            borderTop: '1px solid #DDE2E6',
            mt: isDesktop ? '0px' : '0px',
            px: isDesktop ? '16px' : '12px',
            py: isDesktop ? '10px' : '8px',
          }}
        >
          <Typography
            sx={{
              color: '#162F3B',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: isDesktop ? '0.875rem' : '0.8125rem',
              lineHeight: isDesktop ? '1.25rem' : '1.125rem',
              letterSpacing: '-0.02em',
              textAlign: 'center',
            }}
          >
            {table.footer?.replace(
              '{CURRENT_MONTH_YEAR}',
              new Date().toLocaleString('en-GB', {
                month: 'long',
                year: 'numeric',
              })
            )}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export const MedicalAidSeoDetailCard = ({ section, isDesktop }) => {
  const iconSize = isDesktop ? '120px' : '92px';
  const iconImageSize = isDesktop ? '60px' : '44px';

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#FFFFFF',
        borderRadius: isDesktop ? '40px' : '28px',
        boxShadow: '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
        boxSizing: 'border-box',
        p: isDesktop ? '40px' : '24px',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isDesktop
            ? 'minmax(120px, 120px) minmax(0, 757px)'
            : '1fr',
          gap: isDesktop ? '40px' : '20px',
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            width: iconSize,
            height: iconSize,
            borderRadius: isDesktop ? '30px' : '24px',
            backgroundColor: '#C7CA00',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Image
            width={68}
            height={68}
            src={section.icon}
            alt="Section icon"
            aria-hidden="true"
            sx={{
              width: iconImageSize,
              height: iconImageSize,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>

        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: isDesktop ? '32px' : '16px',
            width: '100%',
            maxWidth: isDesktop ? '757px' : 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isDesktop ? '32px' : '12px',
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: isDesktop ? '2.5rem' : '1.5rem',
                lineHeight: isDesktop ? '2.75rem' : '1.875rem',
                letterSpacing: '-0.03em',
                color: '#162F3B',
                whiteSpace: 'pre-line',
              }}
            >
              {section.title}
            </Typography>

            <Typography
              component="p"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: isDesktop ? '1.125rem' : '1rem',
                lineHeight: isDesktop ? '1.75rem' : '1.5rem',
                letterSpacing: '-0.02em',
                color: '#162F3B',
                whiteSpace: 'pre-line',
              }}
            >
              {section.description}
            </Typography>
          </Box>

          <MedicalAidSeoDetailTable
            table={section.table}
            isDesktop={isDesktop}
          />

          {section.footer ? (
            <Typography
              component="p"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: isDesktop ? '1.125rem' : '1rem',
                lineHeight: isDesktop ? '1.75rem' : '1.5rem',
                letterSpacing: '-0.02em',
                color: '#162F3B',
                whiteSpace: 'pre-line',
              }}
            >
              {section.footer}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
